import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

const COOKIE_ENABLE = 'a42-ac-enable-animations';
const COOKIE_DISABLE = 'a42-ac-disable-animations';

let animationsAllowed = true;

(function registerGsapPluginsOnce() {
    if (typeof window === 'undefined') return;

    if (!window.__ST_REGISTERED__) {
        try {
            gsap.registerPlugin(ScrollTrigger);
            window.__ST_REGISTERED__ = true;
        } catch {
            // noop
        }
    }
})();

export default class MotionControl {
    static init() {
        this.animationCookieName = 'a42-ac-animations';
        this.animationsEnabled = this.readAnimationCookie();
        this.reduceMotionMedia = window.matchMedia?.('(prefers-reduced-motion: reduce)');
        this.prefersReducedMotion = !!this.reduceMotionMedia?.matches;

        if (this.reduceMotionMedia) {
            const listener = (event) => this.handlePrefersReducedMotionChange(event);
            if (this.reduceMotionMedia.addEventListener) {
                this.reduceMotionMedia.addEventListener('change', listener);
            } else if (this.reduceMotionMedia.addListener) {
                this.reduceMotionMedia.addListener(listener);
            }
            this._reduceMotionListener = listener;
        }

        this.applyInitialPreferences();
    }

    static applyInitialPreferences() {
        if (this.prefersReducedMotion) {
            this.setAnimationsDisabled({ persist: false });
            return;
        }

        if (this.animationsEnabled === COOKIE_DISABLE) {
            this.setAnimationsDisabled({ persist: false });
        } else {
            this.setAnimationsEnabled({ persist: false });
        }
    }

    static handleAnimationChange(selectedValue) {
        if (selectedValue === COOKIE_DISABLE) {
            this.setAnimationsDisabled();
        } else {
            this.setAnimationsEnabled();
        }
    }

    static handlePrefersReducedMotionChange(event) {
        this.prefersReducedMotion = event.matches;
        if (event.matches) {
            this.setAnimationsDisabled({ persist: false });
        } else {
            this.applyInitialPreferences();
        }
    }

    static setAnimationsEnabled({ persist = true } = {}) {
        if (this.prefersReducedMotion) {
            this.setAnimationsDisabled({ persist });
            return;
        }

        if (persist) {
            this.createAnimationCookie(COOKIE_ENABLE);
        }

        this.animationsEnabled = COOKIE_ENABLE;
        animationsAllowed = true;
        window.animationsEnabled = true;
        this.setRootAnimClass(true);
    }

    static setAnimationsDisabled({ persist = true } = {}) {
        if (persist) {
            this.createAnimationCookie(COOKIE_DISABLE);
        }

        this.animationsEnabled = COOKIE_DISABLE;
        animationsAllowed = false;
        window.animationsEnabled = false;
        this.setRootAnimClass(false);
        this.killScrollTriggers();
    }

    static setRootAnimClass(isOn) {
        if (typeof document === 'undefined') return;
        const root = document.documentElement;
        if (isOn) root.classList.add('js-anim');
        else root.classList.remove('js-anim');
    }

    static killScrollTriggers() {
        const triggers = ScrollTrigger.getAll();
        if (!triggers.length) return;
        triggers.forEach((trigger) => trigger.kill());
    }

    static createAnimationCookie(value) {
        const date = new Date();
        date.setTime(date.getTime() + 180 * 24 * 60 * 60 * 1000);
        document.cookie = `${
            this.animationCookieName
        }=${value}; expires=${date.toUTCString()}; path=/`;
    }

    static readAnimationCookie() {
        const nameEQ = `${this.animationCookieName}=`;
        const ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') c = c.substring(1);
            if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length);
        }
        return COOKIE_ENABLE;
    }
}

export const motionEnabled = () => animationsAllowed;

export const fx = {
    to(target, vars = {}) {
        if (motionEnabled()) return gsap.to(target, vars);
        gsap.set(target, stripTiming(vars));
        vars?.onComplete?.();
        return null;
    },
    from(target, vars = {}) {
        if (motionEnabled()) return gsap.from(target, vars);
        vars?.onComplete?.();
        return null;
    },
    fromTo(target, fromVars = {}, toVars = {}) {
        if (motionEnabled()) return gsap.fromTo(target, fromVars, toVars);
        gsap.set(target, { ...fromVars, ...stripTiming(toVars) });
        toVars?.onComplete?.();
        return null;
    },
    set(target, vars = {}) {
        return gsap.set(target, vars);
    },
    timeline(vars = {}) {
        if (motionEnabled()) return gsap.timeline(vars);
        const dummy = {
            to(target, v = {}) {
                gsap.set(target, stripTiming(v));
                v?.onComplete?.();
                return this;
            },
            fromTo(target, f = {}, t = {}) {
                gsap.set(target, { ...f, ...stripTiming(t) });
                t?.onComplete?.();
                return this;
            },
            set(target, v = {}) {
                gsap.set(target, v);
                return this;
            },
            add(callback) {
                if (typeof callback === 'function') callback();
                return this;
            },
        };
        return dummy;
    },
    matchMedia(queries) {
        if (motionEnabled()) {
            return gsap.matchMedia(queries);
        }
        return {
            add: () => {},
            revert: () => {},
        };
    },
    clearProps: { clearProps: 'all' },
    clearDisplay: { display: '' },
};

function stripTiming(vars = {}) {
    const rest = { ...vars };
    delete rest.duration;
    delete rest.delay;
    delete rest.ease;
    delete rest.stagger;
    delete rest.repeat;
    delete rest.yoyo;
    return rest;
}
